import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getUserSubscription, 
  getBillingHistory, 
  cancelSubscription,
  SUBSCRIPTION_PLANS 
} from '../../utils/stripeService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import SubscriptionCheckout from '../../components/SubscriptionCheckout';

const SubscriptionManagement = () => {
  const [currentPlan, setCurrentPlan] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [hasHadTrial, setHasHadTrial] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const { user, userProfile } = useAuth();

  useEffect(() => {
    const loadSubscriptionData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Check if user is admin first
        if (userProfile?.role === 'admin') {
          console.log('🔧 User is admin, setting isAdmin to true');
          setIsAdmin(true);
          setLoading(false);
          return;
        }

        console.log('🔧 User is not admin, checking subscription...');
        console.log('🔧 User profile:', userProfile);

        // Load current subscription
        const subscriptionResult = await getUserSubscription();
        console.log('📋 Subscription result:', subscriptionResult);
        console.log('📋 Subscription result success:', subscriptionResult.success);
        console.log('📋 Subscription data exists:', !!subscriptionResult.subscription);
        
        if (subscriptionResult.success && subscriptionResult.subscription) {
          const subscription = subscriptionResult.subscription;
          console.log('📋 Raw subscription data:', subscription);
          console.log('📋 Subscription source - ID:', subscription.id);
          console.log('📋 Subscription source - Stripe ID:', subscription.stripe_subscription_id);
          console.log('📋 Subscription source - Status:', subscription.status);
          
          // Check if this is a trial that hasn't been paid yet
          const isUnpaidTrial = subscription.status === 'trialing' && !subscription.created_at;
          const trialEndDate = new Date(subscription.current_period_end);
          const now = new Date();
          const isTrialExpired = trialEndDate < now;
          
          console.log('🔧 Trial status:', {
            status: subscription.status,
            isUnpaidTrial,
            trialEndDate,
            isTrialExpired,
            now
          });
          
          // If trial is expired or unpaid, don't set currentPlan so user sees upgrade offer
          if (subscription.status === 'trialing' && isTrialExpired) {
            console.log('🔧 Trial expired, showing upgrade offer');
            // Don't set currentPlan, let user see upgrade offer
          } else {
            setCurrentPlan({
              id: subscription.plan || 'premium',
              name: SUBSCRIPTION_PLANS[subscription.plan]?.name || 'All Access Premium',
              price: SUBSCRIPTION_PLANS[subscription.plan]?.price || 5.00,
              status: subscription.status,
              subscriptionDate: subscription.created_at ? new Date(subscription.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : 'Trial Started',
              nextBilling: new Date(subscription.current_period_end).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }),
              autoRenewal: subscription.cancel_at_period_end !== true,
              subscriptionId: subscription.stripe_subscription_id,
              isTrialing: subscription.status === 'trialing',
              trialEndsAt: subscription.status === 'trialing' ? trialEndDate : null
            });
          }
        }

        // Load billing history
        const historyResult = await getBillingHistory();
        if (historyResult.success) {
          setBillingHistory(historyResult.history);
          // Check if user has had a trial before by looking at billing history
          setHasHadTrial(historyResult.history.length > 0);
        }

      } catch (err) {
        console.error('Error loading subscription data:', err);
        setError('Failed to load subscription data');
      } finally {
        setLoading(false);
      }
    };

    loadSubscriptionData();
  }, [user, userProfile]);

  const handleCancelSubscription = async () => {
    if (!currentPlan?.subscriptionId) {
      console.error('No subscription ID available');
      return;
    }

    if (!confirm('Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your current billing period.')) {
      return;
    }

    try {
      setCanceling(true);
      const result = await cancelSubscription(currentPlan.subscriptionId);
      if (result.success) {
        // Update the current plan to reflect cancellation
        setCurrentPlan(prev => ({
          ...prev,
          autoRenewal: false,
          status: 'canceled'
        }));
        alert('Your subscription has been canceled. You will retain access until the end of your current billing period.');
      } else {
        console.error('Failed to cancel subscription:', result.error);
        alert('Failed to cancel subscription. Please try again or contact support.');
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      alert('Failed to cancel subscription. Please try again or contact support.');
    } finally {
      setCanceling(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16 pb-20 md:pb-8">
          <div className="container mx-auto px-4 py-8">
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading subscription data...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16 pb-20 md:pb-8">
          <div className="container mx-auto px-4 py-8">
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
                <h2 className="text-xl font-bold text-foreground mb-2">Error Loading Data</h2>
                <p className="text-muted-foreground mb-6">{error}</p>
                <Button onClick={() => window.location.reload()}>
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show admin view for admin users
  if (isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16 pb-20 md:pb-8">
          <div className="container mx-auto px-4 py-8 max-w-4xl">
            {/* Breadcrumb */}
            <nav className="flex items-center space-x-2 text-sm text-muted-foreground mb-8">
              <Link to="/user-profile-settings" className="hover:text-foreground transition-colors">
                Profile
              </Link>
              <Icon name="ChevronRight" size={16} />
              <span className="text-foreground">Subscription</span>
            </nav>

            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-foreground mb-2">Subscription Management</h1>
              <p className="text-muted-foreground">
                Admin account with premium access
              </p>
            </div>

            {/* Admin Premium Access Card */}
            <div className="bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-xl p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="Shield" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-foreground">Administrator Access</h2>
                      <p className="text-muted-foreground">Full premium features included</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                      Admin
                    </span>
                  </div>
                </div>
              </div>

              {/* Admin Features */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-foreground mb-3">Access Details</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Account Type</span>
                      <span className="text-foreground">Administrator</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Premium Access</span>
                      <span className="font-medium text-green-600">Unlimited</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Billing</span>
                      <span className="text-foreground">Not applicable</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-foreground mb-3">Admin Features</h3>
                  <div className="space-y-2">
                    {[
                      'Access to all games',
                      'Admin dashboard access',
                      'User management',
                      'System administration'
                    ].map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Icon name="Check" size={14} className="text-green-500" />
                        <span className="text-sm text-muted-foreground">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-center gap-2">
                  <Icon name="Info" size={16} className="text-blue-600 dark:text-blue-400" />
                  <span className="text-sm text-blue-800 dark:text-blue-200">
                    As an administrator, you have full access to all premium features without requiring a subscription.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show checkout for users without subscription
  if (!currentPlan && user && showCheckout) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16 pb-20 md:pb-8">
          <div className="container mx-auto px-4 py-8">
            <div className="mb-6">
              <Button
                variant="outline"
                onClick={() => setShowCheckout(false)}
                className="mb-4"
              >
                <Icon name="ArrowLeft" size={16} className="mr-2" />
                Back
              </Button>
            </div>
            <SubscriptionCheckout
              hasHadTrial={hasHadTrial}
              onSuccess={(result) => {
                console.log('Subscription created:', result);
                // Reload the page to show the new subscription
                window.location.reload();
              }}
              onError={(error) => {
                console.error('Subscription error:', error);
              }}
              onCancel={() => {
                setShowCheckout(false);
              }}
            />
          </div>
        </main>
      </div>
    );
  }

  // Debug logging before render
  console.log('🔧 Final render state:', {
    isAdmin,
    currentPlan,
    loading,
    error,
    showCheckout,
    userProfile
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16 pb-20 md:pb-8">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-muted-foreground mb-8">
            <Link to="/user-profile-settings" className="hover:text-foreground transition-colors">
              Profile
            </Link>
            <Icon name="ChevronRight" size={16} />
            <span className="text-foreground">Subscription</span>
          </nav>

          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Subscription Management</h1>
            <p className="text-muted-foreground">
              Manage your gaming subscription and billing
            </p>
          </div>

          {/* No Subscription - Show Premium Offer */}
          {!currentPlan && !isAdmin && (
            <div className="space-y-8">
              {/* Premium Plan Offer */}
              <div className="bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-xl p-8">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                    <Icon name="Crown" size={32} className="text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground mb-2">Upgrade to Premium</h2>
                  <p className="text-muted-foreground">
                    Unlock unlimited access to all games and premium features
                  </p>
                </div>

                {/* Price */}
                <div className="text-center mb-8">
                  <div className="text-4xl font-bold text-foreground mb-2">
                    $5<span className="text-lg text-muted-foreground">/month</span>
                  </div>
                  {!hasHadTrial && (
                    <div className="inline-flex items-center gap-2 bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200 px-3 py-1 rounded-full text-sm font-medium">
                      <Icon name="Gift" size={16} />
                      7-day free trial
                    </div>
                  )}
                </div>

                {/* Features */}
                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  {SUBSCRIPTION_PLANS.premium.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-5 h-5 bg-primary/10 rounded-full flex items-center justify-center">
                        <Icon name="Check" size={12} className="text-primary" />
                      </div>
                      <span className="text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <div className="text-center">
                  <Button
                    size="lg"
                    onClick={() => setShowCheckout(true)}
                    className="px-8"
                  >
                    <Icon name="Crown" size={16} className="mr-2" />
                    {hasHadTrial ? 'Subscribe Now' : 'Start Free Trial'}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-3">
                    Cancel anytime. No long-term commitment.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Has Subscription - Show Subscription Details */}
          {currentPlan && (
            <div className="space-y-8">
              {/* Current Subscription Card */}
              <div className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <Icon name="Crown" size={20} className="text-primary" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-foreground">{currentPlan.name}</h2>
                        <p className="text-muted-foreground">${currentPlan.price}/month</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        currentPlan.status === 'active' 
                          ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200'
                          : currentPlan.status === 'trialing'
                          ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200'
                          : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200'
                      }`}>
                        <div className={`w-1.5 h-1.5 rounded-full ${
                          currentPlan.status === 'active' ? 'bg-green-500' :
                          currentPlan.status === 'trialing' ? 'bg-blue-500' : 'bg-red-500'
                        }`} />
                        {currentPlan.status === 'active' ? 'Active' :
                         currentPlan.status === 'trialing' ? 'Trial' :
                         currentPlan.status === 'canceled' ? 'Canceled' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleCancelSubscription}
                    disabled={canceling || !currentPlan.autoRenewal}
                  >
                    {canceling ? (
                      <>
                        <Icon name="Loader2" size={14} className="animate-spin mr-2" />
                        Canceling...
                      </>
                    ) : (
                      <>
                        <Icon name="XCircle" size={14} className="mr-2" />
                        {currentPlan.autoRenewal ? 'Cancel Subscription' : 'Canceled'}
                      </>
                    )}
                  </Button>
                </div>

                {/* Subscription Details */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-foreground mb-3">Subscription Details</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Started</span>
                        <span className="text-foreground">{currentPlan.subscriptionDate}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Next Billing</span>
                        <span className="text-foreground">{currentPlan.nextBilling}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Auto Renewal</span>
                        <span className={`font-medium ${currentPlan.autoRenewal ? 'text-green-600' : 'text-red-600'}`}>
                          {currentPlan.autoRenewal ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-foreground mb-3">Premium Features</h3>
                    <div className="space-y-2">
                      {SUBSCRIPTION_PLANS.premium.features.slice(0, 4).map((feature, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Icon name="Check" size={14} className="text-green-500" />
                          <span className="text-sm text-muted-foreground">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Trial Notice */}
                {currentPlan.isTrialing && (
                  <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Icon name="Clock" size={16} className="text-blue-600 dark:text-blue-400" />
                      <span className="text-sm text-blue-800 dark:text-blue-200">
                        You're currently on a free trial. Your trial ends on {currentPlan.nextBilling}. 
                        {currentPlan.autoRenewal ? ' You will be charged $5.00 when the trial ends.' : ' Complete your payment to continue after the trial.'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Cancellation Notice */}
                {!currentPlan.autoRenewal && !currentPlan.isTrialing && (
                  <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Icon name="AlertTriangle" size={16} className="text-yellow-600 dark:text-yellow-400" />
                      <span className="text-sm text-yellow-800 dark:text-yellow-200">
                        Your subscription will end on {currentPlan.nextBilling}. You can reactivate anytime before then.
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Billing History - Only show for paying subscribers, not trials */}
              {billingHistory.length > 0 && !currentPlan.isTrialing && (
                <div className="bg-card border border-border rounded-xl p-6">
                  <h2 className="text-xl font-bold text-foreground mb-4">Billing History</h2>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 text-sm font-medium text-muted-foreground">Date</th>
                          <th className="text-left py-3 text-sm font-medium text-muted-foreground">Description</th>
                          <th className="text-left py-3 text-sm font-medium text-muted-foreground">Amount</th>
                          <th className="text-left py-3 text-sm font-medium text-muted-foreground">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {billingHistory.map((transaction, index) => (
                          <tr key={index} className="border-b border-border last:border-b-0">
                            <td className="py-3 text-sm text-foreground">
                              {new Date(transaction.created * 1000).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </td>
                            <td className="py-3 text-sm text-foreground">{transaction.description}</td>
                            <td className="py-3 text-sm text-foreground">
                              ${(transaction.amount / 100).toFixed(2)}
                            </td>
                            <td className="py-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                transaction.status === 'succeeded' 
                                  ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200'
                                  : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200'
                              }`}>
                                {transaction.status === 'succeeded' ? 'Paid' : 'Failed'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SubscriptionManagement;
